<template>
  <div
    class="iq-side-panel-shell"
    data-testid="iq-side-panel-shell"
    role="complementary"
    aria-label="IQ assistant side panel"
  >
    <IQSidePanel
      :messages="panelMessages"
      :user-name="userFirstName"
      :waiting="isWaitingForReply"
      :attachments="attachments"
      :mention-source="searchMentionableEmployees"
      @submit="handleSubmit"
      @stop="handleStop"
      @new-chat="handleNewChat"
      @close="handleClose"
      @maximize="handleMaximize"
      @retry="handleRetry"
      @add-attachment="handleAddAttachment"
      @paste-attachment="handleAddAttachment"
      @remove-attachment="handleRemoveAttachment"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import {
  getAskAiAccessId,
  getAskAiAccountId,
  mapAskAiAttachmentForCard,
  mapAskAiAttachmentForIQ,
  uploadAskAiAttachment,
} from "@/api/askAiConversationApi";
import { fetchIQConversations } from "@/api/askIQApi";
import { getMentionableEmployees } from "@/api/employeeAPI";
import store from "@/store";
import { useIQOverlayStore } from "@/store/pinia/iqOverlay";
import { useIQSidePanelStore } from "@/store/pinia/iqSidePanel";
import { useIQChatStore } from "@components/IQOverlay/store/iqChatStore";
import IQSidePanel from "@components/IQSidePanel/IQSidePanel.vue";
import { MAX_ASK_ATTACHMENTS } from "@components/AskAQuestionPrompt/resolveAttachmentPreview";

const chatStore = useIQChatStore();
const iqOverlayStore = useIQOverlayStore();
const iqSidePanelStore = useIQSidePanelStore();

const activeConversationId = ref(null);
const attachments = ref([]);
// The newest question sent, kept so Retry can re-send it after an error.
const lastRequest = ref(null);
// Bumped on New Chat / close so a reply that lands afterwards cannot revive the old thread id.
let sessionEpoch = 0;
// Covers the window between submit and the store flipping to a loading state,
// while attachments upload and the request is dispatched. Without it the input
// looks idle and a second submit starts a concurrent send.
const isSending = ref(false);

const panelMessages = computed(() => chatStore.messages);

const isWaitingForReply = computed(
  () =>
    isSending.value ||
    chatStore.isGenerating ||
    chatStore.isLoading ||
    chatStore.isThinking
);

const userFirstName = computed(() => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) {
      return "";
    }
    const user = JSON.parse(raw);
    const fromEmployee = user?.employee?.first_name || user?.employee?.firstName;
    if (fromEmployee) {
      return String(fromEmployee).trim();
    }
    if (user?.firstName) {
      return String(user.firstName).trim();
    }
    const name = (user?.name || "").trim();
    return name ? name.split(/\s+/)[0] : "";
  } catch {
    return "";
  }
});

const isAskAiConversationsEnabled = () =>
  store.getters["featureFlags/ideaMDP582"] === true &&
  getAskAiAccountId() != null &&
  getAskAiAccessId() != null;

const loadIQConversations = async () => {
  const permissionId = getAskAiAccessId();
  const accountId = getAskAiAccountId();
  if (permissionId == null || permissionId === "" || accountId == null) {
    return;
  }
  try {
    await fetchIQConversations(permissionId, accountId);
  } catch (error) {
    console.error("Failed to fetch IQ conversations:", error);
  }
};

const resetSession = () => {
  sessionEpoch += 1;
  activeConversationId.value = null;
  attachments.value = [];
  lastRequest.value = null;
};

const rememberConversationId = (epoch, result) => {
  if (epoch === sessionEpoch && result?.conversationId) {
    activeConversationId.value = result.conversationId;
  }
};

const searchMentionableEmployees = (query) => {
  const accountId = getAskAiAccountId();
  if (accountId == null) {
    return Promise.resolve([]);
  }
  return getMentionableEmployees({ account_id: accountId, query });
};

const handleNewChat = () => {
  resetSession();
  chatStore.clearChat();
};

const uploadAttachmentItem = async (item) => {
  try {
    const uploaded = await uploadAskAiAttachment({ file: item.file });
    item.card = mapAskAiAttachmentForCard(uploaded);
    item.iqRef = mapAskAiAttachmentForIQ(uploaded);
    if (item.card?.previewUrl) {
      item.previewUrl = item.card.previewUrl;
    }
  } catch (error) {
    console.error("Ask AI attachment upload failed:", error);
    item.uploadError = true;
  } finally {
    item.uploading = false;
  }
};

const handleAddAttachment = (payload) => {
  if (!payload || typeof payload !== "object") {
    return;
  }
  if (attachments.value.length >= MAX_ASK_ATTACHMENTS) {
    return;
  }
  // Upload as soon as the file is added so the storage reference is ready by
  // the time the user hits send; the send step only awaits a still-running one.
  const item = {
    ...payload,
    iqRef: null,
    card: null,
    uploading: Boolean(payload.file),
    uploadError: false,
  };
  attachments.value = [...attachments.value, item];
  if (item.file) {
    item.uploadPromise = uploadAttachmentItem(item);
  }
};

const handleRemoveAttachment = (index) => {
  if (typeof index !== "number" || index < 0 || index >= attachments.value.length) {
    return;
  }
  attachments.value = attachments.value.filter((_, itemIndex) => itemIndex !== index);
};

const handleStop = () => {
  chatStore.stopGenerating();
};

const sendWithAttachments = async (prompt, pendingAttachments, mentions = []) => {
  const epoch = sessionEpoch;

  try {
    const uploadedCards = [];
    const iqAttachments = [];

    for (const item of pendingAttachments) {
      if (!item?.file) {
        continue;
      }
      // Uploads normally finished on add; wait for any still in flight.
      if (item.uploadPromise) {
        await item.uploadPromise.catch(() => {});
      }

      let card = item.card;
      let iqRef = item.iqRef;
      if (!iqRef) {
        // Late retry when the eager upload failed or never started.
        try {
          const uploaded = await uploadAskAiAttachment({ file: item.file });
          card = mapAskAiAttachmentForCard(uploaded);
          iqRef = mapAskAiAttachmentForIQ(uploaded);
        } catch (error) {
          console.error("Ask AI attachment upload failed:", error);
        }
      }

      if (card?.id) {
        uploadedCards.push(card);
      }
      if (iqRef) {
        iqAttachments.push(iqRef);
      }
    }

    const attachedFileCount = pendingAttachments.filter((item) => item?.file).length;

    if (attachedFileCount > 0 && iqAttachments.length === 0) {
      // Every upload failed or returned no storage key, so IQ would receive no
      // file. Stop the send rather than asking text-only and leave the
      // attachments in place so the user can retry.
      chatStore.handleAskAiConversationFailure(
        new Error("The attachment could not be uploaded. Please try again.")
      );
      return;
    }

    attachments.value = [];

    chatStore.addUserMessage(prompt, { attachmentRefs: uploadedCards, mentions });

    const result = await chatStore.generateAttachmentResponse({
      question: prompt,
      attachments: iqAttachments,
      conversationId: activeConversationId.value,
      onAfterAskSuccess: loadIQConversations,
      mentions,
    });

    rememberConversationId(epoch, result);
  } catch (error) {
    console.error("Ask AI attachment send failed:", error);
    chatStore.handleAskAiConversationFailure(error);
  }
};

const submitPrompt = async (prompt, pendingAttachments, mentions = []) => {
  lastRequest.value = { prompt, attachments: pendingAttachments, mentions };
  const useAttachmentAsk =
    pendingAttachments.length > 0 && isAskAiConversationsEnabled();

  if (useAttachmentAsk) {
    isSending.value = true;
    try {
      await sendWithAttachments(prompt, pendingAttachments, mentions);
    } finally {
      isSending.value = false;
    }
    return;
  }

  const epoch = sessionEpoch;
  chatStore.addUserMessage(prompt, { mentions });
  attachments.value = [];
  const result = await chatStore.generateAIResponse(
    prompt,
    5,
    null,
    null,
    loadIQConversations,
    activeConversationId.value,
    { mentions }
  );
  rememberConversationId(epoch, result);
};

const handleSubmit = async (text, mentions = []) => {
  const prompt = String(text || "").trim();
  if (!prompt || isWaitingForReply.value) {
    return;
  }
  await submitPrompt(prompt, [...attachments.value], mentions);
};

// Drops the failed exchange (error row + its question bubble) so the re-send does not duplicate it.
const dropFailedExchange = (prompt) => {
  const thread = [...chatStore.messages];
  if (thread[thread.length - 1]?.isError) {
    thread.pop();
  }
  const last = thread[thread.length - 1];
  if (last?.type === "user" && last.content === prompt) {
    thread.pop();
  }
  chatStore.setMessages(thread);
};

const handleRetry = async () => {
  const request = lastRequest.value;
  if (!request || isWaitingForReply.value) {
    return;
  }
  dropFailedExchange(request.prompt);
  await submitPrompt(request.prompt, request.attachments, request.mentions);
};

const handleClose = () => {
  resetSession();
  chatStore.clearChat();
  iqSidePanelStore.closeSidePanel();
};

const handleMaximize = () => {
  iqOverlayStore.openOverlay();
  iqSidePanelStore.closeSidePanel();
};

onMounted(() => {
  loadIQConversations();
});
</script>

<style scoped>
.iq-side-panel-shell {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-self: stretch;
  height: 100vh;
  max-height: 100vh;
  min-height: 0;
}
</style>
